import { BimIndexClient } from "./bimIndexClient";
import type { OpenResult, Requests } from "./protocol";

/**
 * Process-wide BIM-index session: one worker, hosting the WASM module and at most
 * one loaded index.
 *
 * An engine keeps decoded pages cached and holds a lock on every file it reads,
 * so every consumer — `window.bimIndex` and the data access layer — goes through
 * this rather than starting a second worker over the same index.
 *
 * A *loaded index* is one use of that worker and not the only one:
 * {@link bimIndexBitmapIds} needs the module and nothing else, so a scene reading
 * a served index — which has no local files to open — still comes through here.
 * `bimIndexInfo()` staying `null` for the whole of such a session is correct, not a
 * sign the worker failed to start.
 */

let client: BimIndexClient | null = null;
let info: OpenResult | null = null;
let opening: Promise<OpenResult> | null = null;
/** Every `open` sent to the current worker and not answered yet, superseded or not. */
let opens = new Set<Promise<unknown>>();
/** The release {@link closeBimIndex} has in flight, which a second close waits on too. */
let releasing: Promise<void> | null = null;

/** The session's worker client, started on first use. */
export function bimIndexClient(): BimIndexClient {
    if (!client) {
        client = new BimIndexClient();
    }
    return client;
}

export function bimIndexInfo(): OpenResult | null {
    return info;
}

/** Load the index, replacing whatever is currently open. */
export async function openBimIndex(params: Requests["open"]["params"]): Promise<OpenResult> {
    const opener = bimIndexClient();
    const pending = opener.call("open", params);
    const inFlight = opens;
    inFlight.add(pending);
    opening = pending;
    try {
        const result = await pending;
        // A close or a newer open may have taken the session over meanwhile.
        if (client === opener && opening === pending) {
            info = result;
        }
        return result;
    } finally {
        inFlight.delete(pending);
        if (opening === pending) {
            opening = null;
        }
    }
}

/**
 * Load the index unless it is already open (or opening) — the idempotent entry
 * point for callers that just need it available, such as the first query through
 * the data access layer.
 */
export function ensureBimIndexOpen(params: Requests["open"]["params"]): Promise<OpenResult> {
    if (info) {
        return Promise.resolve(info);
    }
    return opening ?? openBimIndex(params);
}

/**
 * Ids of the union of the serialized roaring bitmaps a served index answered with.
 *
 * Both legs are transfers rather than copies: the bodies go over as buffers and the
 * ids come back as one, so a multi-megabyte set crosses twice without being copied
 * either time, and the deserialize and the materialize both happen off the main
 * thread. **A `bitmaps` or `unionWith` array that owns its buffer is detached when
 * this returns** — hand over only what is no longer needed here.
 *
 * Starts the worker if it is not running and opens nothing, which is the served
 * case: there is no local index to open.
 */
export function bimIndexBitmapIds(bitmaps: Uint8Array[], unionWith?: Uint32Array): Promise<Uint32Array> {
    const transfer = transferableBuffers(unionWith ? [...bitmaps, unionWith] : bitmaps);
    return bimIndexClient().call("bitmapIds", { bitmaps, unionWith }, transfer);
}

/** `ids` as portable roaring bytes. Copied rather than transferred: `ids` is the caller's. */
export function bimIndexSerializedIds(ids: Uint32Array): Promise<Uint8Array> {
    return bimIndexClient().call("serializeIds", { ids });
}

/**
 * Ids as portable roaring bytes — {@link bimIndexBitmapIds} reversed. Copied rather than
 * transferred: the input is usually a live `SelectionSet`'s `sortedIds`.
 */
export function bimIndexIdsBitmap(ids: Uint32Array): Promise<Uint8Array> {
    return bimIndexClient().call("idsBitmap", { ids });
}

/**
 * The distinct buffers of `views`, skipping any view that does not span its own.
 *
 * A transfer neuters the whole `ArrayBuffer` rather than the view, so a slice of a
 * shared one would detach its siblings, and the same buffer listed twice is a
 * `DataCloneError`. Those views are copied by the structured clone instead — which
 * costs a copy and is always correct.
 */
function transferableBuffers(views: ArrayBufferView[]): Transferable[] {
    const buffers = new Set<ArrayBufferLike>();
    for (const view of views) {
        if (view.byteOffset === 0 && view.byteLength === view.buffer.byteLength) {
            buffers.add(view.buffer);
        }
    }
    return [...buffers] as Transferable[];
}

/**
 * Forget the index and terminate the worker — the only way to hand its linear
 * memory back to the browser.
 *
 * The session is free of it immediately; the worker gets asked to let go of its
 * OPFS files first, because terminating runs no code there and the files stay
 * locked until the browser tears the realm down. Every `open` still in flight is
 * waited out first: the worker does not serialize messages, so a `close` landing
 * mid-open would release nothing and the open would then lock the files.
 *
 * No timeout: a worker busy with a long scan answers `close` once the scan is done,
 * and resolving before that would tell the caller the files are free while they are not.
 *
 * @returns Once the files are released, or the worker has died. A close called while
 *   another is in flight resolves with it. Never rejects.
 */
export function closeBimIndex(): Promise<void> {
    const closing = client;
    const pendingOpens = [...opens];
    client = null;
    info = null;
    opening = null;
    opens = new Set();
    if (closing) {
        const previous = releasing;
        const release = (async () => {
            await Promise.allSettled(pendingOpens);
            // A worker that failed to close is terminated all the same.
            await closing.call("close", undefined).catch(() => {});
            closing.terminate();
            await previous;
        })();
        releasing = release;
        void release.then(() => {
            if (releasing === release) {
                releasing = null;
            }
        });
    }
    return releasing ?? Promise.resolve();
}
